import React, { useCallback, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  Chip,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Pagination,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityIcon from "@mui/icons-material/Visibility";
import PeopleIcon from "@mui/icons-material/People";
import FilterListIcon from "@mui/icons-material/FilterList";
import EventIcon from "@mui/icons-material/Event";
import { TableContainer } from "@mui/material";
import { styled } from "@mui/system";

import BackBtn from "../../widgets/BackBtn";
import CustomCircularP from "../../widgets/CustomCircularP";
import ExportVisitorsButton from "./ExportVisitorsButton";
import useApiEp from "../../hooks/useApiEp";
import { extractDate, extractTime, getErrorResponse } from "../../utils/util";

export const ScrollTableContainer = styled(TableContainer)({
  overflowX: "auto",
});

const Visitors = ({ type }) => {
  const [reload, setReload] = useState(false);
  const [page, setPage] = useState(1);
  const [eventFilter, setEventFilter] = useState("");
  const [sourceFilter, setSourceFilter] = useState("");
  const [sq, setSq] = useState("");
  const navigate = useNavigate();

  const theme = useTheme();

  const { data, isLoading, isError, error } = useApiEp(
    `/admin/get-visitors?type=${type}&page=${page}&sq=${sq}&r=${reload}&eventCategory=${eventFilter}&sourceType=${sourceFilter}`
  );

  const { data: sourceList } = useApiEp(`/event/get-visitor-sources`);

  const debounce = (func, delay) => {
    let timeoutId;
    return (...args) => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      timeoutId = setTimeout(() => {
        func.apply(null, args);
      }, delay);
    };
  };

  const handleSearch = (e) => {
    setSq(e.target.value);
    setPage(1);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedHandleSearch = useCallback(debounce(handleSearch, 400), []);

  const pageChange = (e, p) => {
    setPage(p);
  };

  const handleSourceChange = (e) => {
    setSourceFilter(e.target.value);
    setPage(1);
  };

  if (isError) {
    return (
      <Box mt={3} px={2}>
        <Alert severity="error">{getErrorResponse(error)}</Alert>
      </Box>
    );
  }

  const titleType = type ? type.charAt(0).toUpperCase() + type.slice(1) : "Visitor";
  const totalVisitors = data?.totalItems || 0;
  const totalPages = data?.totalPages || Math.ceil(totalVisitors / 30) || 1;

  const getLocationStr = (user) => {
    const parts = [user.city?.name, user.state?.name, user.country?.name].filter(Boolean);
    return parts.length > 0 ? parts.join(", ") : "-";
  };

  return (
    <Box sx={{ pb: 6 }}>
      <CustomCircularP show={isLoading} />

      {/* Top Navigation & Header */}
      <Stack direction="row" alignItems="center" spacing={2} mb={3}>
        <BackBtn />
        <Box flex={1}>
          <Typography
            variant="h4"
            fontWeight="800"
            color="#1E293B"
            sx={{ fontSize: { xs: "1.5rem", md: "1.75rem" }, letterSpacing: "-0.02em" }}
          >
            {titleType}s Directory
          </Typography>
          <Typography variant="body2" color="#64748B" fontWeight="500">
            Manage & monitor all registered event {titleType.toLowerCase()}s ({totalVisitors.toLocaleString()} total records)
          </Typography>
        </Box>
      </Stack>

      {/* Metric Summary Cards Bar */}
      <Grid container spacing={2.5} mb={3.5}>
        <Grid item xs={12} sm={4}>
          <Card
            elevation={0}
            sx={{
              p: 2.25,
              borderRadius: "16px",
              border: "1px solid #E2E8F0",
              backgroundColor: "#FFFFFF",
              display: "flex",
              alignItems: "center",
              gap: 2,
              boxShadow: "0px 2px 8px rgba(15, 23, 42, 0.04)",
            }}
          >
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: "12px",
                backgroundColor: "#EEF4FF",
                color: "#2563EB",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <PeopleIcon />
            </Box>
            <Box>
              <Typography variant="caption" color="#64748B" fontWeight="600" textTransform="uppercase" letterSpacing="0.04em">
                Total Registered {titleType}s
              </Typography>
              <Typography variant="h5" fontWeight="800" color="#1E293B" sx={{ lineHeight: 1.2, mt: 0.25 }}>
                {totalVisitors.toLocaleString()}
              </Typography>
            </Box>
          </Card>
        </Grid>

        <Grid item xs={12} sm={4}>
          <Card
            elevation={0}
            sx={{
              p: 2.25,
              borderRadius: "16px",
              border: "1px solid #E2E8F0",
              backgroundColor: "#FFFFFF",
              display: "flex",
              alignItems: "center",
              gap: 2,
              boxShadow: "0px 2px 8px rgba(15, 23, 42, 0.04)",
            }}
          >
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: "12px",
                backgroundColor: "#F0FDF4",
                color: "#16A34A",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <FilterListIcon />
            </Box>
            <Box>
              <Typography variant="caption" color="#64748B" fontWeight="600" textTransform="uppercase" letterSpacing="0.04em">
                Source Filter
              </Typography>
              <Typography variant="h5" fontWeight="800" color="#1E293B" sx={{ lineHeight: 1.2, mt: 0.25, fontSize: "1.1rem" }} noWrap>
                {sourceFilter || "All Sources"}
              </Typography>
            </Box>
          </Card>
        </Grid>

        <Grid item xs={12} sm={4}>
          <Card
            elevation={0}
            sx={{
              p: 2.25,
              borderRadius: "16px",
              border: "1px solid #E2E8F0",
              backgroundColor: "#FFFFFF",
              display: "flex",
              alignItems: "center",
              gap: 2,
              boxShadow: "0px 2px 8px rgba(15, 23, 42, 0.04)",
            }}
          >
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: "12px",
                backgroundColor: "#F3E8FF",
                color: "#7C3AED",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <EventIcon />
            </Box>
            <Box>
              <Typography variant="caption" color="#64748B" fontWeight="600" textTransform="uppercase" letterSpacing="0.04em">
                Active Event
              </Typography>
              <Typography variant="h5" fontWeight="800" color="#1E293B" sx={{ lineHeight: 1.2, mt: 0.25 }}>
                PMTX 2026
              </Typography>
            </Box>
          </Card>
        </Grid>
      </Grid>

      {/* Main Data Table Card */}
      <Card
        elevation={0}
        sx={{
          borderRadius: "16px",
          border: "1px solid #E2E8F0",
          backgroundColor: "#FFFFFF",
          boxShadow: "0px 4px 20px rgba(15, 23, 42, 0.04)",
          overflow: "hidden",
        }}
      >
        {/* Search & Source Filter Header Toolbar */}
        <Box
          sx={{
            p: 2.5,
            borderBottom: "1px solid #E2E8F0",
            backgroundColor: "#FAFBFD",
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: { xs: "stretch", md: "center" },
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} flex={1} maxWidth={{ md: 650 }}>
            {/* Source Select Dropdown */}
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel id="source-filter-label">Registration Source</InputLabel>
              <Select
                labelId="source-filter-label"
                value={sourceFilter}
                label="Registration Source"
                onChange={handleSourceChange}
                sx={{ borderRadius: "10px", backgroundColor: "#FFFFFF" }}
              >
                <MenuItem value="">
                  <em>All Sources</em>
                </MenuItem>
                {Array.isArray(sourceList)
                  ? sourceList.map((src, idx) => (
                      <MenuItem key={idx} value={src}>
                        {src}
                      </MenuItem>
                    ))
                  : sourceList?.data?.map((src, idx) => (
                      <MenuItem key={idx} value={src}>
                        {src}
                      </MenuItem>
                    ))}
              </Select>
            </FormControl>

            {/* Search Field */}
            <TextField
              placeholder="Search by name, email, or company..."
              size="small"
              onChange={debouncedHandleSearch}
              fullWidth
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "10px",
                  backgroundColor: "#FFFFFF",
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: "#94A3B8" }} />
                  </InputAdornment>
                ),
              }}
            />
          </Stack>

          {/* Export Action Button */}
          <ExportVisitorsButton filter={type} sourceFilter={sourceFilter} sq={sq} />
        </Box>

        {/* Visitors Table */}
        <Box sx={{ overflowX: "auto" }}>
          <Table sx={{ minWidth: 1100 }} size="small" aria-label="visitors table">
            <TableHead>
              <TableRow sx={{ backgroundColor: "#F8FAFC" }}>
                <TableCell sx={{ fontWeight: 700, color: "#475569", py: 1.15, fontSize: "0.8rem", width: "25%", minWidth: 240 }}>Attendee Name</TableCell>
                <TableCell sx={{ fontWeight: 700, color: "#475569", py: 1.15, fontSize: "0.8rem", width: "22%", minWidth: 220 }}>Email & Contact</TableCell>
                <TableCell sx={{ fontWeight: 700, color: "#475569", py: 1.15, fontSize: "0.8rem", width: "18%", minWidth: 180 }}>Company</TableCell>
                <TableCell sx={{ fontWeight: 700, color: "#475569", py: 1.15, fontSize: "0.8rem", width: "18%", minWidth: 170 }}>Location</TableCell>
                <TableCell sx={{ fontWeight: 700, color: "#475569", py: 1.15, fontSize: "0.8rem", width: "12%", minWidth: 130 }}>Registered On</TableCell>
                <TableCell sx={{ fontWeight: 700, color: "#475569", py: 1.15, fontSize: "0.8rem", width: 80, textAlign: "right" }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!data?.data || data.data.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 5 }}>
                    <Typography variant="body1" color="#64748B" fontWeight="600">
                      No {titleType.toLowerCase()} records found.
                    </Typography>
                    <Typography variant="caption" color="#94A3B8">
                      Try adjusting your filter options or search terms.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                data.data.map((user) => {
                  const fullName = `${user.title || ""} ${user.firstName || ""} ${user.lastName || ""}`.trim() || "N/A";
                  const initial = user.firstName ? user.firstName[0].toUpperCase() : "U";

                  return (
                    <TableRow
                      key={user._id}
                      sx={{
                        "&:hover": { backgroundColor: "#F8FAFC" },
                        transition: "background-color 0.15s ease-in-out",
                      }}
                    >
                      {/* Name & Avatar */}
                      <TableCell sx={{ py: 1 }}>
                        <Stack direction="row" alignItems="center" spacing={1.25}>
                          <Avatar
                            src={user.profile_image || ""}
                            alt={fullName}
                            sx={{
                              width: 32,
                              height: 32,
                              backgroundColor: "#EEF4FF",
                              color: "#2563EB",
                              fontWeight: 700,
                              fontSize: "0.85rem",
                              border: "1px solid #DBEAFE",
                            }}
                          >
                            {initial}
                          </Avatar>
                          <Box>
                            <Link
                              to={`/visitor/${user._id}`}
                              style={{ textDecoration: "none", color: "#2563EB", fontWeight: 700 }}
                            >
                              <Typography variant="subtitle2" fontWeight="700" color="#2563EB" sx={{ fontSize: "0.85rem", "&:hover": { textDecoration: "underline" } }}>
                                {fullName}
                              </Typography>
                            </Link>
                            {user.visitorGroup && (
                              <Typography variant="caption" color="#64748B" display="block" sx={{ fontSize: "0.725rem" }}>
                                Group: {user.visitorGroup}
                              </Typography>
                            )}
                          </Box>
                        </Stack>
                      </TableCell>

                      {/* Email & Mobile */}
                      <TableCell sx={{ py: 1 }}>
                        <Typography variant="body2" color="#334155" fontWeight="500" sx={{ fontSize: "0.825rem" }}>
                          {user.email || user.contactEmail?.value || "-"}
                        </Typography>
                        <Typography variant="caption" color="#64748B" display="block" sx={{ fontSize: "0.725rem" }}>
                          {user.mobileNo || user.mobile || user.phone?.value || user.phone || user.phoneNo || user.contactNo || "-"}
                        </Typography>
                      </TableCell>

                      {/* Company Name */}
                      <TableCell sx={{ py: 1 }}>
                        <Typography variant="body2" color="#334155" fontWeight="600" sx={{ fontSize: "0.825rem" }}>
                          {(typeof user.companyName === "string" ? user.companyName : user.companyName?.value) || user.company || user.institute || user.institution || user.organization || "-"}
                        </Typography>
                      </TableCell>

                      {/* Location */}
                      <TableCell sx={{ py: 1 }}>
                        <Typography variant="body2" color="#475569" fontWeight="500" sx={{ fontSize: "0.825rem" }}>
                          {getLocationStr(user)}
                        </Typography>
                      </TableCell>

                      {/* Registered On */}
                      <TableCell sx={{ py: 1 }}>
                        <Typography variant="body2" color="#334155" fontWeight="500" sx={{ fontSize: "0.825rem" }}>
                          {extractDate(user.createdOn)}
                        </Typography>
                        <Typography variant="caption" color="#94A3B8" display="block" sx={{ fontSize: "0.725rem" }}>
                          {extractTime(user.createdOn)}
                        </Typography>
                      </TableCell>

                      {/* Actions */}
                      <TableCell sx={{ py: 1, textAlign: "right" }}>
                        <Tooltip title={`View ${titleType} Details`}>
                          <Link to={`/visitor/${user._id}`}>
                            <IconButton size="small" sx={{ color: "#2563EB", p: 0.5, "&:hover": { backgroundColor: "#EEF4FF" } }}>
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Link>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </Box>

        {/* Footer & Pagination Bar */}
        <Box
          sx={{
            p: 2.5,
            borderTop: "1px solid #E2E8F0",
            backgroundColor: "#FAFBFD",
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Typography variant="body2" color="#64748B" fontWeight="500">
            Showing page {page} of {totalPages} ({totalVisitors.toLocaleString()} {titleType.toLowerCase()}s)
          </Typography>

          {data && totalVisitors > 0 && (
            <Pagination
              page={page}
              onChange={pageChange}
              count={totalPages}
              color="primary"
              shape="rounded"
            />
          )}
        </Box>
      </Card>
    </Box>
  );
};

export default Visitors;
